/**
 * AdminSubscriptionTransfersTab — admin queue for Nigeria manual
 * subscription bank transfers.
 *
 * Feb 2026 user spec: add transfer option for Nigeria subscriptions
 * with WhatsApp receipt to +2348064002451. This tab shows pending
 * transfers and lets an admin Approve (activates the subscription) or
 * Reject with a reason.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, CheckCircle2, XCircle, Landmark, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { authFetch } from '../utils/authUtils';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const StatusPill = ({ status }) => {
  const map = {
    awaiting_receipt: { label: 'Awaiting receipt', cls: 'bg-slate-700 text-slate-300' },
    awaiting_admin: { label: 'Awaiting admin', cls: 'bg-amber-500/20 text-amber-200 border border-amber-400/40' },
    approved: { label: 'Approved', cls: 'bg-emerald-500/20 text-emerald-200 border border-emerald-400/40' },
    rejected: { label: 'Rejected', cls: 'bg-rose-500/20 text-rose-200 border border-rose-400/40' },
  };
  const s = map[status] || { label: status, cls: 'bg-slate-700 text-slate-300' };
  return <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold ${s.cls}`}>{s.label}</span>;
};

const AdminSubscriptionTransfersTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [filter, setFilter] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/subscription-transfers`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setRows(Array.isArray(data?.transfers) ? data.transfers : []);
    } catch (e) {
      toast.error(`Failed to load: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (row) => {
    if (!window.confirm(`Approve ${row.reference} for ${row.user_email} (${row.currency} ${Number(row.amount).toLocaleString()}, ${row.plan_months}m)? This activates their subscription.`)) return;
    setBusyId(row.transfer_id);
    const tid = toast.loading('Approving…');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/subscription-transfers/${row.transfer_id}/approve`, { method: 'POST' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Approve failed');
      toast.success(`✓ Approved — expires ${new Date(data.expires_at).toLocaleDateString()}`, { id: tid });
      load();
    } catch (e) {
      toast.error(`Approve failed: ${e.message}`, { id: tid });
    } finally {
      setBusyId('');
    }
  };

  const reject = async (row) => {
    const reason = window.prompt(`Reject ${row.reference}? Enter reason (shown to user):`);
    if (!reason) return;
    setBusyId(row.transfer_id);
    const tid = toast.loading('Rejecting…');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/subscription-transfers/${row.transfer_id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Reject failed');
      toast.success(`✓ Rejected`, { id: tid });
      load();
    } catch (e) {
      toast.error(`Reject failed: ${e.message}`, { id: tid });
    } finally {
      setBusyId('');
    }
  };

  const filtered = filter === 'pending'
    ? rows.filter((r) => ['awaiting_receipt', 'awaiting_admin'].includes(r.status))
    : filter === 'approved'
      ? rows.filter((r) => r.status === 'approved')
      : filter === 'rejected'
        ? rows.filter((r) => r.status === 'rejected')
        : rows;

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="admin-sub-transfers-tab">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div>
          <CardTitle className="text-white flex items-center gap-2">
            <Landmark className="w-5 h-5 text-emerald-300" />
            Subscription Bank Transfers (Nigeria)
          </CardTitle>
          <p className="text-xs text-slate-400 mt-1">
            Approving a transfer activates the user's subscription for the plan_months × 30 days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {['pending', 'approved', 'rejected', 'all'].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`text-xs px-2.5 py-1 rounded-md border ${filter === k ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-slate-700/40 border-slate-600 text-slate-300 hover:bg-slate-700'}`}
              data-testid={`admin-sub-tr-filter-${k}`}
            >
              {k[0].toUpperCase() + k.slice(1)}
            </button>
          ))}
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="border-slate-600 text-slate-200">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && rows.length === 0 ? (
          <div className="text-slate-400 text-sm py-8 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-slate-400 text-sm py-8 text-center" data-testid="admin-sub-tr-empty">
            No transfers in this bucket.
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((row) => {
              const isBusy = busyId === row.transfer_id;
              const showActions = ['awaiting_receipt', 'awaiting_admin'].includes(row.status);
              return (
                <div
                  key={row.transfer_id}
                  className="rounded-lg border border-slate-700 bg-slate-900/50 p-3"
                  data-testid={`admin-sub-tr-row-${row.transfer_id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white text-sm font-mono font-bold">{row.reference}</span>
                        <StatusPill status={row.status} />
                        <span className="text-[10px] text-slate-400">· {row.plan_months}m {row.plan_tier}</span>
                      </div>
                      <p className="text-xs text-slate-300 mt-1">
                        {row.user_email || row.user_id} · <strong className="text-emerald-300">₦{Number(row.amount).toLocaleString()}</strong>
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Created {new Date(row.created_at).toLocaleString()}
                        {row.receipt_sent_at && ` · Receipt sent ${new Date(row.receipt_sent_at).toLocaleTimeString()}`}
                        {row.approved_at && ` · Approved ${new Date(row.approved_at).toLocaleString()}`}
                        {row.rejected_at && ` · Rejected ${new Date(row.rejected_at).toLocaleString()}`}
                      </p>
                      {row.reject_reason && (
                        <p className="text-[11px] text-rose-300 mt-0.5">Reason: {row.reject_reason}</p>
                      )}
                    </div>
                    {showActions && (
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          onClick={() => approve(row)}
                          disabled={isBusy}
                          className="bg-emerald-600 hover:bg-emerald-500 text-white"
                          data-testid={`admin-sub-tr-approve-${row.transfer_id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => reject(row)}
                          disabled={isBusy}
                          className="border-rose-700 text-rose-200 hover:bg-rose-900/30"
                          data-testid={`admin-sub-tr-reject-${row.transfer_id}`}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminSubscriptionTransfersTab;
